import { emitSwAuthTelemetry } from './swAuthTelemetry.ts'

export type SwAuthSyncMetricSnapshot = {
  syncPosted: number
  preflightMatched: number
  preflightTimeout: number
  reloadOk: number
  reloadMismatchRetry: number
  reloadMismatchExhausted: number
  reloadWithoutEpoch: number
  idbReadFailed: number
  preflightReadFailed: number
}

export type SwAuthReloadMismatchKind = 'uid' | 'issued_at'

const metrics: SwAuthSyncMetricSnapshot = {
  syncPosted: 0,
  preflightMatched: 0,
  preflightTimeout: 0,
  reloadOk: 0,
  reloadMismatchRetry: 0,
  reloadMismatchExhausted: 0,
  reloadWithoutEpoch: 0,
  idbReadFailed: 0,
  preflightReadFailed: 0,
}

function bump(key: keyof SwAuthSyncMetricSnapshot): void {
  metrics[key] += 1
}

function snapshotMetrics(): SwAuthSyncMetricSnapshot {
  return { ...metrics }
}

export function getSwAuthSyncMetrics(): Readonly<SwAuthSyncMetricSnapshot> {
  return snapshotMetrics()
}

export function resetSwAuthSyncMetrics(): void {
  for (const key of Object.keys(metrics) as (keyof SwAuthSyncMetricSnapshot)[]) {
    metrics[key] = 0
  }
}

export function recordSwAuthSyncPosted(preflight: {
  matched: boolean
  waitMs: number
  hasEpoch: boolean
  preflightReadFailed?: boolean
}): void {
  bump('syncPosted')
  if (preflight.hasEpoch) {
    if (preflight.matched) bump('preflightMatched')
    else bump('preflightTimeout')
    if (preflight.preflightReadFailed) bump('preflightReadFailed')
  }
  emitSwAuthTelemetry({
    kind: 'sync_posted',
    ...preflight,
    metrics: snapshotMetrics(),
  })
}

export function recordSwAuthReloadOk(attempt: number): void {
  bump('reloadOk')
  emitSwAuthTelemetry({ kind: 'reload_ok', attempt, metrics: snapshotMetrics() })
}

export function recordSwAuthReloadMismatchRetry(
  attempt: number,
  mismatchKind: SwAuthReloadMismatchKind,
): void {
  bump('reloadMismatchRetry')
  emitSwAuthTelemetry({
    kind: 'reload_mismatch_retry',
    attempt,
    mismatchKind,
    metrics: snapshotMetrics(),
  })
}

export function recordSwAuthReloadMismatchExhausted(
  mismatchKind: SwAuthReloadMismatchKind,
): void {
  bump('reloadMismatchExhausted')
  emitSwAuthTelemetry({
    kind: 'reload_mismatch_exhausted',
    mismatchKind,
    metrics: snapshotMetrics(),
  })
}

export function recordSwAuthReloadWithoutEpoch(): void {
  bump('reloadWithoutEpoch')
  emitSwAuthTelemetry({ kind: 'reload_without_epoch', metrics: snapshotMetrics() })
}

export function recordSwAuthIdbReadFailed(message: string): void {
  bump('idbReadFailed')
  emitSwAuthTelemetry({ kind: 'idb_read_failed', message, metrics: snapshotMetrics() })
}

type SwAuthSyncMetricsHost = {
  __ghSwAuthSyncMetrics?: {
    get: () => Readonly<SwAuthSyncMetricSnapshot>
    reset: () => void
  }
}

if (import.meta.env.DEV && typeof globalThis !== 'undefined') {
  ;(globalThis as SwAuthSyncMetricsHost).__ghSwAuthSyncMetrics = {
    get: getSwAuthSyncMetrics,
    reset: resetSwAuthSyncMetrics,
  }
}
